import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { Worksheet } from "@/services/worksheet.service";

// ============================================================================
// Constants
// ============================================================================

/** Subject display names for badges */
export const SUBJECT_LABELS: Record<string, string> = {
  mathematics: "Mathematics",
  social_studies: "Social Studies",
  english_language_arts: "English Language Arts",
  science: "Science",
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format grade for display.
 */
export function formatGrade(grade: string): string {
  if (grade === "K") return "Kindergarten";
  return `Grade ${grade}`;
}

// ============================================================================
// Components
// ============================================================================

/**
 * Skeleton loading state for WorksheetCard.
 */
export function WorksheetCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <Skeleton className="h-5 w-3/4" />
      </CardHeader>
      <CardContent>
        <div className="flex gap-2">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-24" />
        </div>
      </CardContent>
    </Card>
  );
}

interface WorksheetCardProps {
  worksheet: Worksheet;
}

/**
 * Card component for displaying a worksheet in the grid.
 */
export function WorksheetCard({ worksheet }: WorksheetCardProps) {
  return (
    <Link
      to={`/worksheets/${worksheet.id}`}
      className="block group"
    >
      <Card className="h-full overflow-hidden transition-colors hover:border-primary/50 hover:shadow-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-base group-hover:text-primary transition-colors line-clamp-2">
            {worksheet.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">
              {formatGrade(worksheet.grade)}
            </Badge>
            <Badge variant="outline">
              {SUBJECT_LABELS[worksheet.subject] ?? worksheet.subject}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
